import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatForDatetimeLocalInput, parseFromDatetimeLocalInput, getOrganizationTimezone, getTimezoneDisplayName } from '../../lib/timezoneUtils';
import {
  Modal,
  ModalDialog,
  ModalClose,
  Typography,
  Box,
  FormControl,
  FormLabel,
  Select,
  Option,
  Input,
  Textarea,
  Button,
  Stack,
  RadioGroup,
  Radio,
  FormHelperText,
  Alert,
} from '@mui/joy';
import { Sparkle as AutoAwesomeIcon } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { GBPPostPreview } from './GBPPostPreview';

import { localSEOError } from '../../lib/localSEOUtils';
import { GBPPostImageField } from './GBPPostImageField';
import type { MediaFile } from './mediaApi';
import type { GBPPost } from '../../types/marketing';

interface EditGBPPostModalProps {
  open: boolean;
  onClose: () => void;
  post: GBPPost | null;
  onSubmit: (postId: number, data: any) => Promise<void>;
}

export const EditGBPPostModal: React.FC<EditGBPPostModalProps> = ({
  open,
  onClose,
  post,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'update',
    cta_type: '',
    cta_link: '',
    status: 'draft',
    scheduled_time: null as Date | null,
  });
  const [selectedMedia, setSelectedMedia] = useState<MediaFile | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [generatingAI, setGeneratingAI] = useState(false);

  const { data: orgData } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const res = await api.get<{ time_zone?: string }>('/api/organizations/current/');
      return res.data;
    },
  });
  const orgTimezone = orgData?.time_zone || getOrganizationTimezone();
  const [aiGenerationError, setAiGenerationError] = useState('');

  // Reset form when post changes
  useEffect(() => {
    if (post && open) {
      setSelectedMedia(null);
      setError('');
      setAiGenerationError('');
      setFormData({
        title: post.title || '',
        content: post.content || '',
        type: post.type || 'update',
        cta_type: post.cta_type || '',
        cta_link: post.cta_link || '',
        status: post.status || 'draft',
        scheduled_time: post.scheduled_time ? new Date(post.scheduled_time) : null,
      });
      // Set selected media if exists - reconstruct combined ID format for backend
      if (post.combined_image_url) {
        let mediaId: string | number = 0;
        let source: 'marketing' | 'production' | undefined;

        if (post.image) {
          mediaId = `marketing_${post.image}`;
          source = 'marketing';
        } else if (post.production_photo) {
          mediaId = `production_${post.production_photo}`;
          source = 'production';
        }

        setSelectedMedia({
          id: mediaId,
          file: post.combined_image_url,
          name: 'Current Image',
          file_type: 'image',
          tags: [],
          uploaded_at: '',
          file_size: 0,
          dimensions: { width: 0, height: 0 },
          usage_count: 0,
          source
        });
      }
    }
  }, [post, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!post) return;

    setError('');
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        scheduled_time: formData.scheduled_time?.toISOString() || null,
        media_id: selectedMedia ? selectedMedia.id : undefined,
        ...(selectedMedia ? {} : { image: null, production_photo: null })
      };
      await onSubmit(post.id, submitData);
      handleClose();
    } catch (err: any) {
      setError(localSEOError(err, 'Failed to update post'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      content: '',
      type: 'update',
      cta_type: '',
      cta_link: '',
      status: 'draft',
      scheduled_time: null,
    });
    setSelectedMedia(null);
    setError('');
    setAiGenerationError('');
    onClose();
  };

  const handleGenerateWithAI = async () => {
    const topic = formData.title || prompt('What topic would you like to generate content about?');
    if (!topic) return;

    setGeneratingAI(true);
    setAiGenerationError('');

    try {
      const response = await api.post('/api/marketing/gbp-posts/generate/', {
        topic,
        post_type: formData.type,
        include_cta: true
      });

      const responseData = response.data as any;
      if (responseData.success && responseData.generated_content) {
        const content = responseData.generated_content;
        
        // Convert cta_type to lowercase since AI returns uppercase (CALL, LEARN_MORE)
        // but backend expects lowercase (call, learn_more)
        const aiCtaType = content.cta_type?.toLowerCase() || formData.cta_type;
        setFormData({
          ...formData,
          title: content.title || formData.title,
          content: content.content || formData.content,
          cta_type: aiCtaType,
          cta_link: aiCtaType === 'call' ? '' : formData.cta_link,
        });
      }
    } catch (err: any) {
      console.error('AI generation error:', err);
      setAiGenerationError(err.response?.data?.error || 'Failed to generate content with AI');
    } finally {
      setGeneratingAI(false);
    }
  };

  const titleMaxLength = 58;
  const contentMaxLength = 1500;

  if (!post) return null;

  return (
    <Modal open={open} onClose={handleClose}>
      <ModalDialog 
        size="lg" 
        sx={{ 
          minWidth: { xs: '90vw', md: 750 },
          maxWidth: 1000,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <ModalClose />
        <Typography level="h4">Edit Google Business Post</Typography>
        
        {post.is_auto_generated && (
          <Alert color="primary" variant="soft" sx={{ mt: 2 }}>
            <AutoAwesomeIcon style={{ marginRight: 8 }} />
            This post was auto-generated
          </Alert>
        )}
        
        <Box sx={{ 
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
          mt: 2
        }}>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              {error && (
                <Alert color="danger">{error}</Alert>
              )}

              <FormControl required>
                <FormLabel>Business Profile</FormLabel>
                <Typography level="body-lg">
                  {post.google_business_profile_name || 'Google Business Profile'}
                </Typography>
              </FormControl>

              <FormControl required>
                <FormLabel>Post Type</FormLabel>
                <RadioGroup
                  orientation="horizontal"
                  sx={{ flexWrap: 'wrap', rowGap: 1 }}
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <Radio value="update" label="Update" />
                  <Radio value="offer" label="Offer" />
                  <Radio value="event" label="Event" />
                  <Radio value="product" label="Product" />
                </RadioGroup>
              </FormControl>

              <FormControl required>
                <FormLabel>Title</FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  slotProps={{
                    input: { maxLength: titleMaxLength }
                  }}
                />
                <FormHelperText>
                  {formData.title.length}/{titleMaxLength} characters
                </FormHelperText>
              </FormControl>

              <FormControl required>
                <FormLabel>Content</FormLabel>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  minRows={4}
                  maxRows={8}
                  slotProps={{
                    textarea: { maxLength: contentMaxLength }
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <FormHelperText sx={{ m: 0 }}>
                    {formData.content.length}/{contentMaxLength} characters
                  </FormHelperText>
                  <Button
                    size="sm"
                    variant="soft"
                    color="primary"
                    startDecorator={<AutoAwesomeIcon />}
                    onClick={handleGenerateWithAI}
                    loading={generatingAI}
                    disabled={generatingAI}
                  >
                    Generate with AI
                  </Button>
                </Box>
                {aiGenerationError && (
                  <Alert color="danger" size="sm" sx={{ mt: 1 }}>
                    {aiGenerationError}
                  </Alert>
                )}
              </FormControl>

              <FormControl>
                <FormLabel>Call-to-Action Type</FormLabel>
                <Select
                  value={formData.cta_type || ''}
                  onChange={(e, value) => {
                    const newCtaType = (value || '') as string;
                    setFormData({
                      ...formData,
                      cta_type: newCtaType,
                      cta_link: newCtaType === 'call' ? '' : formData.cta_link,
                    });
                  }}
                >
                  <Option value="">No CTA</Option>
                  <Option value="book">Book</Option>
                  <Option value="order">Order Online</Option>
                  <Option value="shop">Shop</Option>
                  <Option value="learn_more">Learn More</Option>
                  <Option value="sign_up">Sign Up</Option>
                  <Option value="call">Call Now</Option>
                </Select>
              </FormControl>

              {formData.cta_type && formData.cta_type !== 'call' && (
                <FormControl>
                  <FormLabel>CTA Link</FormLabel>
                  <Input
                    type="url"
                    value={formData.cta_link}
                    onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                    placeholder="https://example.com"
                  />
                </FormControl>
              )}

              <GBPPostImageField key={post.id} value={selectedMedia} onChange={setSelectedMedia} />

              <FormControl>
                <FormLabel>Schedule</FormLabel>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_time ? formatForDatetimeLocalInput(new Date(formData.scheduled_time), orgTimezone) : ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    scheduled_time: e.target.value ? parseFromDatetimeLocalInput(e.target.value, orgTimezone) : null
                  })}
                  placeholder="Choose a scheduled time"
                />
                <FormHelperText>Times are in {getTimezoneDisplayName(orgTimezone)}. Scheduled posts require a future time; drafts stay unpublished.</FormHelperText>
              </FormControl>

              <FormControl required>
                <FormLabel>Status</FormLabel>
                <RadioGroup
                  orientation="horizontal"
                  sx={{ flexWrap: 'wrap', rowGap: 1 }}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <Radio value="draft" label="Draft" />
                  <Radio value="pending_review" label="Pending Review" />
                  <Radio value="scheduled" label="Scheduled" />
                </RadioGroup>
              </FormControl>

              <GBPPostPreview post={formData as Partial<GBPPost>} imageUrl={selectedMedia?.file} profileName={post?.google_business_profile_name} />

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                <Button variant="plain" onClick={handleClose}>
                  Cancel
                </Button>
                <Button type="submit" loading={submitting}>
                  Update Post
                </Button>
              </Box>
            </Stack>
          </form>
        </Box>
      </ModalDialog>
    </Modal>
  );
};